import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { ThemeProvider, readStoredTheme } from "./theme";
import { Toaster, toast } from "./components/Toaster";
import { ErrorBoundary, ReloadNotice } from "./components/ErrorBoundary";
import "./index.css";

// apply the saved theme before first paint to avoid a flash
document.documentElement.dataset.theme = readStoredTheme();

const isUnauthorized = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 401;

/** A dead session leaves the UI half-broken; clearing `me` routes to /login. */
function handleAuthExpiry() {
  queryClient.setQueryData(["me"], null);
}

const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    // window-focus refetch stays on (default): boards are shared, and
    // returning to the tab is the natural moment to catch up
    queries: { retry: 1, staleTime: 30_000 },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (isUnauthorized(error)) handleAuthExpiry();
    },
  }),
  // Surface every failed mutation — otherwise edits fail silently.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isUnauthorized(error)) {
        handleAuthExpiry();
        toast("Your session expired — please log in again.");
        return;
      }
      toast(error instanceof Error ? error.message : "Something went wrong");
    },
  }),
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <ErrorBoundary fallback={(e) => <ReloadNotice error={e} />}>
            <App />
          </ErrorBoundary>
          <Toaster />
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);